import cx from 'classnames';
import {FormikProps} from 'formik';
import {get, isEmpty, isNumber, map} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Accordion, AccordionDetails, AccordionTitle, Button, DatePicker, Icon, InputFormik, Radio, SelectFormik} from 'components';
import {UserContext} from 'context';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {SpecialDayRepeat, SpecialDayWorkingHoursItem, WorkingHours as WorkingHoursType} from 'types';
import {workingHoursUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

import {MultiInputInterval} from '../../MultiInputInterval';
import {WorkingHoursItemTitle} from '../../WorkingHoursItemTitle';
import {FIELDS} from '../../constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

const SPECIAL_HOURS_FIELDS = {
    DAY: 'day',
    MONTH: 'month',
    YEAR: 'year',
    FROM: 'from',
    TO: 'to',
    NAME: 'name',
    REPEAT: 'repeat'
};

enum SpecialDayType {
    DayOff = 'dayOff',
    SpecialHours = 'specialHours'
}

export enum SpecialHoursItemMode {
    Create = 'create',
    Update = 'update'
}

type Props = {
    mode: SpecialHoursItemMode;
    index?: number;
    defaultValue?: Partial<SpecialDayWorkingHoursItem>;
    onClose?: () => void;
    onRemove?: () => void;
} & FormikProps<WorkingHoursType>;

export const SpecialHoursItem: React.FC<Props> = ({
    index, mode, values, defaultValue, setFieldTouched, errors, setFieldValue, onRemove, onClose
}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const isCreating = useMemo(() => mode === SpecialHoursItemMode.Create, [mode]);
    const [expanded, setExpanded] = useState(false);
    const newItemPath = useMemo(() => FIELDS.NEW_SPECIAL_HOURS_ITEM, []);
    const existingItemPath = useMemo(() => `${FIELDS.SPECIAL_HOURS}[${index}]`, [index]);
    const specialHoursPath = useMemo(() => isCreating ? newItemPath : existingItemPath, [isCreating, newItemPath, existingItemPath]);

    const item: SpecialDayWorkingHoursItem =
        useMemo(() => get(values, specialHoursPath) || defaultValue, [values, specialHoursPath, defaultValue]);
    const hasEmptyIntervals = useMemo(() => isEmpty(item?.intervals), [item]);
    const isInvalid = useMemo(() =>
        mode === SpecialHoursItemMode.Update && !isEmpty(get(errors, existingItemPath)), [mode, errors, existingItemPath]);
    const [itemType, setItemType] =
        useState<SpecialDayType>(hasEmptyIntervals ? SpecialDayType.DayOff : SpecialDayType.SpecialHours);

    const itemMoment = useMemo(() => {
        if (isNumber(item?.year) && isNumber(item?.month) && isNumber(item?.day)) {
            return moment(new Date(item.year, item.month, item.day), dateFormat).tz(timeZone);
        } else {
            return null;
        }
    }, [item, dateFormat, timeZone]);

    const repeatOptions: Option[] = useMemo(() => map(SpecialDayRepeat, (value) => ({
        id: value,
        name: t(T_KEYS.LABELS[buildTranslationKey(value)])
    })), [t]);

    const isValidInterval = useMemo(() => {
        const isValidEmptyInterval = itemType === SpecialDayType.DayOff || !hasEmptyIntervals;
        return isValidEmptyInterval && workingHoursUtils.validateSpecialDay(item, t).isValid;
    }, [item, itemType, hasEmptyIntervals, t]);

    const handleCancel = useCallback(() => {
        setFieldValue(specialHoursPath, defaultValue);
        onClose();
    }, [setFieldValue, specialHoursPath, onClose, defaultValue]);

    const handleAdd = useCallback(() => {
        const result = itemType === SpecialDayType.DayOff ? {...item, intervals: []} : item;
        setFieldValue(`${FIELDS.SPECIAL_HOURS}[${values.specialHours.length}]`, result);
        handleCancel();
    }, [item, itemType, values, setFieldValue, handleCancel]);

    const handleDateChange = useCallback((_moment: moment.Moment) => {
        setFieldValue(`${specialHoursPath}.${SPECIAL_HOURS_FIELDS.DAY}`, _moment.get('date'));
        setFieldValue(`${specialHoursPath}.${SPECIAL_HOURS_FIELDS.MONTH}`, _moment.get('month'));
        setFieldValue(`${specialHoursPath}.${SPECIAL_HOURS_FIELDS.YEAR}`, _moment.get('year'));
        setFieldTouched(specialHoursPath, true);
    }, [setFieldValue, setFieldTouched, specialHoursPath]);

    useEffect(() => {
        if (isCreating) {
            setFieldValue(newItemPath, {...defaultValue});
            setFieldTouched(newItemPath, false);
        }
    }, [isCreating, defaultValue, newItemPath, setFieldTouched, setFieldValue]);

    useEffect(() => {
        if (itemType === SpecialDayType.DayOff && !hasEmptyIntervals) {
            setFieldValue(specialHoursPath, {...item, intervals: []});
        }
    }, [isCreating, itemType, specialHoursPath, setFieldValue, hasEmptyIntervals, item]);

    return (
        <Accordion
            shadow={false}
            expanded={isCreating || expanded}
            defaultExpanded={isCreating}
            className={cx(
                'tw-flex-grow tw-mb-4 tw-py-4 tw-border tw-rounded-lg',
                {
                    'tw-border-alert': isInvalid,
                    'tw-border-default': !isInvalid
                })}
            onChange={setExpanded}
        >
            <AccordionTitle iconPlacement='right' styled={false}>
                <div className='tw-flex tw-items-center'>
                    {isCreating ?
                        t(T_KEYS.SPECIAL_HOURS.NEW_SPECIAL_DAY.TITLE) :
                        <WorkingHoursItemTitle item={item} showExtraInfo={!expanded} />}
                    {!isCreating &&
                        <div className='tw-ml-auto tw-mr-4'>
                            <Icon name='delete' color='alert' stopPropagation button onClick={onRemove} />
                        </div>}
                </div>
            </AccordionTitle>
            <AccordionDetails className='tw-mt-4'>
                <div className='tw-mt-2 tw-flex tw-flex-col sm:tw-items-baseline'>
                    <div className='sm:tw-flex tw-items-start'>
                        <Radio
                            id={`special-day-type-${index}-day-off`}
                            name={`special-day-type-${index}`}
                            labelPlacement='left'
                            className='tw-mb-4 sm:tw-mr-4'
                            label={t(T_KEYS.SPECIAL_HOURS.NEW_SPECIAL_DAY.OPTIONS.DAY_OFF)}
                            checked={itemType === SpecialDayType.DayOff}
                            value={SpecialDayType.DayOff}
                            onSelect={setItemType}
                        />
                        <Radio
                            id={`special-day-type-${index}-special-day`}
                            name={`special-day-type-${index}`}
                            labelPlacement='left'
                            label={t(T_KEYS.SPECIAL_HOURS.NEW_SPECIAL_DAY.OPTIONS.SPECIAL_HOURS)}
                            checked={itemType === SpecialDayType.SpecialHours}
                            value={SpecialDayType.SpecialHours}
                            onSelect={setItemType}
                        />
                    </div>
                    <InputFormik
                        className='sm:tw-w-96 tw-mb-4'
                        validate={!isCreating}
                        name={`${specialHoursPath}.${SPECIAL_HOURS_FIELDS.NAME}`}
                        label={t(T_KEYS.LABELS.NAME)}
                    />
                    <div className='tw-flex -sm:tw-flex-col tw-mb-4'>
                        <DatePicker
                            className='sm:tw-w-56 sm:tw-mr-4 -sm:tw-mb-4'
                            overlayClassName='-sm:tw-left-1/2 -sm:tw--translate-x-1/2'
                            openDirection='top'
                            value={itemMoment}
                            format={dateFormat}
                            onChange={handleDateChange}
                        />
                        <SelectFormik
                            className='sm:tw-w-36 sm:tw-mr-4 -sm:tw-mb-4'
                            placeholder={t(T_KEYS.LABELS.REPEAT)}
                            options={repeatOptions}
                            name={`${specialHoursPath}.${SPECIAL_HOURS_FIELDS.REPEAT}`}
                            valueAs='id'
                        />
                    </div>
                </div>
                {itemType === SpecialDayType.SpecialHours &&
                    <MultiInputInterval
                        name={`${specialHoursPath}.${FIELDS.INTERVALS}`}
                        itemsPath={`${specialHoursPath}.${FIELDS.INTERVALS}`}
                        values={values}
                    />}
                {isCreating &&
                    <div className='tw-flex -sm:tw-flex-col sm:tw-justify-end tw-mt-4'>
                        <Button className='sm:tw-mr-2 -sm:tw-mb-2' onClick={handleCancel}>{t(T_KEYS_COMMON.BUTTONS.CANCEL)}</Button>
                        <Button disabled={!isValidInterval} color='primary' onClick={handleAdd}>{t(T_KEYS_COMMON.BUTTONS.CREATE)}</Button>
                    </div>}
            </AccordionDetails>
        </Accordion>
    );
};